import React, { useEffect } from "react";
import { LoginPage, CustomBrandLogo } from "@ippon/shared-auth";
import { useDispatch, useSelector } from "react-redux";
import { login as loginThunk } from "@/store/authSlice";
import type { AppDispatch, RootState } from "@/store";
import { useNavigate } from "react-router-dom";
import { isAuthenticated } from "@/utils/authUtils";
import { CLIENT_ACCESS_MESSAGES, isClientAccessCode } from "@/api/contractApi";

const Login: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const loading = useSelector(
    (state: RootState) => state.auth.loginState.loading
  );

  useEffect(() => {
    if (isAuthenticated()) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // 契約による強制ログアウト（SPEC §7.4）の理由を 1 回だけ表示する
  const [initialErrorMessage] = React.useState(() => {
    const reason = sessionStorage.getItem("logoutReason");
    if (!isClientAccessCode(reason)) return "";
    sessionStorage.removeItem("logoutReason");
    return CLIENT_ACCESS_MESSAGES[reason];
  });

  return (
    <LoginPage
      renderLogo={<CustomBrandLogo label="設定管理" />}
      loading={loading}
      initialErrorMessage={initialErrorMessage}
      defaultRedirectPath="/"
      forgotPasswordTo="/checkmail"
      onSubmit={async ({ username, password, recaptchaToken }) => {
        try {
          // reCAPTCHA トークンは LoginPage(shared-auth) が送信直前に取得して渡す。
          const action = (await dispatch(
            loginThunk({ username, password, recaptchaToken })
          )) as any;

          const status = action?.payload?.data?.status;
          if (status === 200) {
            // Tokens (IdToken/RefreshToken) are stored via slice on fulfilled
            return { ok: true };
          }

          // 403(reCAPTCHA不合格)・429(ロックアウト/レート制限)は BE の個別
          // メッセージを表示。401(認証失敗)等はユーザー列挙対策で汎用文言のまま。
          const beStatus = action?.payload?.status;
          const beMessage = action?.payload?.message;
          // 契約前・契約終了（SPEC §9.12）は code 付きの 401。Cognito 認証成功後にだけ返るため
          // 利用者の存在推測にはならず、契約状態の文言をそのまま出す。
          const accessCode: unknown = action?.payload?.code;
          if (beStatus === 401 && isClientAccessCode(accessCode)) {
            return { ok: false, message: CLIENT_ACCESS_MESSAGES[accessCode] };
          }
          if (
            (beStatus === 403 || beStatus === 429) &&
            typeof beMessage === "string" &&
            beMessage
          ) {
            return { ok: false, message: beMessage };
          }

          return { ok: false, message: "ログインに失敗しました" };
        } catch (e: any) {
          return { ok: false, message: "ログインに失敗しました" };
        }
      }}
    />
  );
};

export default Login;
